export interface ContentAwareScaleResult {
  pixels: Uint8ClampedArray;
  width: number;
  height: number;
}

interface Plane {
  pixels: Uint8ClampedArray;
  protect: Uint8Array;
  width: number;
  height: number;
}

function energy(plane: Plane, x: number, y: number): number {
  const sample = (sampleX: number, sampleY: number, channel: number) => plane.pixels[(Math.max(0, Math.min(plane.height - 1, sampleY)) * plane.width + Math.max(0, Math.min(plane.width - 1, sampleX))) * 4 + channel] ?? 0;
  let gradient = 0;
  for (let channel = 0; channel < 3; channel += 1) {
    gradient += Math.abs(sample(x + 1, y, channel) - sample(x - 1, y, channel));
    gradient += Math.abs(sample(x, y + 1, channel) - sample(x, y - 1, channel));
  }
  return gradient + (plane.protect[y * plane.width + x] ?? 0) / 255 * 1_000_000;
}

function minimumVerticalSeam(plane: Plane): Int32Array {
  const previous = new Float64Array(plane.width);
  const current = new Float64Array(plane.width);
  const parents = new Int8Array(plane.width * plane.height);
  for (let x = 0; x < plane.width; x += 1) previous[x] = energy(plane, x, 0);
  for (let y = 1; y < plane.height; y += 1) {
    for (let x = 0; x < plane.width; x += 1) {
      let parent = x;
      let best = previous[x] ?? Number.POSITIVE_INFINITY;
      if (x > 0 && (previous[x - 1] ?? Number.POSITIVE_INFINITY) < best) { best = previous[x - 1]!; parent = x - 1; }
      if (x + 1 < plane.width && (previous[x + 1] ?? Number.POSITIVE_INFINITY) < best) { best = previous[x + 1]!; parent = x + 1; }
      current[x] = energy(plane, x, y) + best;
      parents[y * plane.width + x] = parent - x;
    }
    previous.set(current);
  }
  let x = 0;
  for (let candidate = 1; candidate < plane.width; candidate += 1) if ((previous[candidate] ?? Infinity) < (previous[x] ?? Infinity)) x = candidate;
  const seam = new Int32Array(plane.height);
  for (let y = plane.height - 1; y >= 0; y -= 1) {
    seam[y] = x;
    x += parents[y * plane.width + x] ?? 0;
  }
  return seam;
}

function removeVerticalSeam(plane: Plane, seam: Int32Array): Plane {
  const width = plane.width - 1;
  const pixels = new Uint8ClampedArray(width * plane.height * 4);
  const protect = new Uint8Array(width * plane.height);
  for (let y = 0; y < plane.height; y += 1) {
    const removed = seam[y] ?? 0;
    for (let x = 0; x < width; x += 1) {
      const sourceX = x < removed ? x : x + 1;
      const sourceOffset = (y * plane.width + sourceX) * 4;
      pixels.set(plane.pixels.subarray(sourceOffset, sourceOffset + 4), (y * width + x) * 4);
      protect[y * width + x] = plane.protect[y * plane.width + sourceX] ?? 0;
    }
  }
  return { pixels, protect, width, height: plane.height };
}

function insertVerticalSeam(plane: Plane, seam: Int32Array): Plane {
  const width = plane.width + 1;
  const pixels = new Uint8ClampedArray(width * plane.height * 4);
  const protect = new Uint8Array(width * plane.height);
  for (let y = 0; y < plane.height; y += 1) {
    const inserted = seam[y] ?? 0;
    for (let x = 0; x < width; x += 1) {
      const sourceX = x <= inserted ? x : x - 1;
      const sourceOffset = (y * plane.width + Math.min(plane.width - 1, sourceX)) * 4;
      const targetOffset = (y * width + x) * 4;
      if (x === inserted + 1) {
        const nextX = Math.min(plane.width - 1, inserted + 1);
        const nextOffset = (y * plane.width + nextX) * 4;
        for (let channel = 0; channel < 4; channel += 1) pixels[targetOffset + channel] = Math.round(((plane.pixels[sourceOffset + channel] ?? 0) + (plane.pixels[nextOffset + channel] ?? 0)) / 2);
        protect[y * width + x] = Math.max(plane.protect[y * plane.width + inserted] ?? 0, plane.protect[y * plane.width + nextX] ?? 0);
      } else {
        pixels.set(plane.pixels.subarray(sourceOffset, sourceOffset + 4), targetOffset);
        protect[y * width + x] = plane.protect[y * plane.width + Math.min(plane.width - 1, sourceX)] ?? 0;
      }
    }
  }
  return { pixels, protect, width, height: plane.height };
}

function transpose(plane: Plane): Plane {
  const pixels = new Uint8ClampedArray(plane.pixels.length);
  const protect = new Uint8Array(plane.protect.length);
  for (let y = 0; y < plane.height; y += 1) for (let x = 0; x < plane.width; x += 1) {
    const target = x * plane.height + y;
    pixels.set(plane.pixels.subarray((y * plane.width + x) * 4, (y * plane.width + x + 1) * 4), target * 4);
    protect[target] = plane.protect[y * plane.width + x] ?? 0;
  }
  return { pixels, protect, width: plane.height, height: plane.width };
}

function resizeWidth(plane: Plane, targetWidth: number): Plane {
  let output = plane;
  while (output.width > targetWidth) output = removeVerticalSeam(output, minimumVerticalSeam(output));
  while (output.width < targetWidth) output = insertVerticalSeam(output, minimumVerticalSeam(output));
  return output;
}

export function contentAwareScalePixels(
  source: Uint8ClampedArray,
  width: number,
  height: number,
  targetWidth: number,
  targetHeight: number,
  protect = new Uint8Array(width * height),
): ContentAwareScaleResult {
  if (![width, height, targetWidth, targetHeight].every((value) => Number.isInteger(value) && value > 0) || source.length !== width * height * 4 || protect.length !== width * height) throw new Error('Content-Aware Scale dimensions are invalid.');
  if (width * height > 2_000_000 || targetWidth * targetHeight > 4_000_000) throw new Error('Content-Aware Scale is limited to 2 megapixel sources and 4 megapixel outputs.');
  if (Math.abs(targetWidth - width) > 256 || Math.abs(targetHeight - height) > 256) throw new Error('Content-Aware Scale is limited to 256 inserted or removed seams per axis; use Resize Image for larger changes.');
  if (width * height * (Math.abs(targetWidth - width) + Math.abs(targetHeight - height)) > 120_000_000) throw new Error('This Content-Aware Scale request requires too much seam analysis. Use smaller steps or Resize Image.');
  if (targetWidth < 2 || targetHeight < 2) throw new Error('Content-Aware Scale output must be at least 2 × 2 pixels.');
  let plane: Plane = { pixels: new Uint8ClampedArray(source), protect: new Uint8Array(protect), width, height };
  plane = resizeWidth(plane, targetWidth);
  plane = transpose(plane);
  plane = resizeWidth(plane, targetHeight);
  plane = transpose(plane);
  return { pixels: plane.pixels, width: plane.width, height: plane.height };
}
